import React, { FC, useEffect, useMemo, useRef, useState, useContext } from "react";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import {
	sharedShiftDialogContentSx,
	sharedShiftDialogHeaderSx,
	sharedShiftDialogPanelSx,
	sharedShiftDialogPaperSx,
} from "@/styles/sharedShiftDialogStyles";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
	Box,
	Button,
	Dialog,
	DialogContent,
	DialogContentText,
	Divider,
	Stack,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";

import { useFetchShiftSummary } from "@/service/ShiftHomeQueries";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";



interface ShiftSummaryDialogueProps {
	groupId: number;
	open: boolean;
	handleClose: (open: boolean) => void;
}

/** Strip tags so we can type/copy safely even if summary contains HTML */
const stripHtml = (s?: string) =>
	(s || "")
		.replace(/<[^>]*>/g, " ")
		.replace(/\s+/g, " ")
		.trim();

export const ShiftSummaryDialogue: FC<ShiftSummaryDialogueProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const { data, isLoading } = useFetchShiftSummary(props.groupId, props.open);

	const htmlSummary = data?.Summary ?? "";
	const plainSummary = useMemo(() => stripHtml(htmlSummary), [htmlSummary]);

	// typing state
	const [typed, setTyped] = useState("");
	const [done, setDone] = useState(false);
	const timerRef = useRef<number | null>(null);

	// copy feedback
	const [copied, setCopied] = useState<"none" | "text" | "html">("none");
	const copiedTimerRef = useRef<number | null>(null);

	const resetCopyToast = () => {
		setCopied("none");
		if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
		copiedTimerRef.current = window.setTimeout(() => setCopied("none"), 1400);
	};

	// reset + type when open + data ready
	useEffect(() => {
		if (!props.open) {
			setTyped("");
			setDone(false);
			setCopied("none");
			if (timerRef.current) window.clearInterval(timerRef.current);
			if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
			return;
		}

		if (isLoading || !plainSummary) {
			setTyped("");
			setDone(false);
			setCopied("none");
			if (timerRef.current) window.clearInterval(timerRef.current);
			return;
		}

		setTyped("");
		setDone(false);
		setCopied("none");

		let i = 0;
		const speedMs = 12;

		if (timerRef.current) window.clearInterval(timerRef.current);
		timerRef.current = window.setInterval(() => {
			i += 1;
			setTyped(plainSummary.slice(0, i));
			if (i >= plainSummary.length) {
				if (timerRef.current) window.clearInterval(timerRef.current);
				setDone(true);
			}
		}, speedMs);

		return () => {
			if (timerRef.current) window.clearInterval(timerRef.current);
		};
	}, [props.open, isLoading, plainSummary]);

	const copyText = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text);
			resetCopyToast();
			setCopied("text");
		} catch {
			// fallback for older/locked-down environments
			const ta = document.createElement("textarea");
			ta.value = text;
			ta.style.position = "fixed";
			ta.style.left = "-9999px";
			document.body.appendChild(ta);
			ta.focus();
			ta.select();
			document.execCommand("copy");
			document.body.removeChild(ta);
			resetCopyToast();
			setCopied("text");
		}
	};

	const handleCopyPlain = () => {
		void copyText(plainSummary);
	};

	const handleCopyHtml = () => {
		void copyText(htmlSummary);
	};

	return (
		<StyledDialog
			open={props.open}
			handleClose={() => props.handleClose(false)}
		>
			{/* Header actions */}
			<Box
				sx={{
					...sharedShiftDialogHeaderSx(theme),
					px: { xs: 1.5, sm: 2 },
					pt: 1.5,
					pb: 1,
					display: "flex",
					flexDirection: { xs: "column", sm: "row" },
					alignItems: { xs: "stretch", sm: "center" },
					justifyContent: "space-between",
					gap: 1,
				}}
			>
				<Box sx={{ minWidth: 0 }}>
					<Typography variant="subtitle1" fontWeight={800} noWrap>
						<Translate String="Auto.Features.Shift.Ui.Components.ShiftSummaryDialogue.L154C62.ShiftSummary">Shift summary</Translate>
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{done ? "Ready" : isLoading ? "Generating…" : "Typing…"}
					</Typography>
				</Box>

				<Stack
					direction="row"
					spacing={1}
					alignItems="center"
					flexWrap="wrap"
					useFlexGap
					sx={{ justifyContent: { xs: "flex-end", sm: "flex-start" } }}
				>
					<Tooltip
						title={
							done ? "Copy summary" : "Wait until the summary finishes typing"
						}
						arrow
					>
						<span>
							<Button
								size="small"
								variant="outlined"
								color="info"
								onClick={handleCopyPlain}
								disabled={!done}
								startIcon={
									copied === "text" ? <CheckIcon /> : <ContentCopyIcon />
								}
								sx={{ textTransform: "none", fontWeight: 700 }}
							>
								{copied === "text" ? "Copied" : "Copy"}
							</Button>
						</span>
					</Tooltip>

					{/* Useful extra: copy formatted summary too (optional) */}
					<Tooltip title={GetLanguageValueAsString(translation, "Auto.Features.Shift.Ui.Components.ShiftSummaryDialogue.L196C21.CopyFormattedHTML", "Copy formatted (HTML)")} arrow>
						<span>
							<Button
								size="small"
								variant="text"
								color="info"
								onClick={handleCopyHtml}
								disabled={!done || !htmlSummary}
								sx={{ textTransform: "none", fontWeight: 700 }}
							>
								{copied === "html" ? "Copied HTML" : "Copy HTML"}
							</Button>
						</span>
					</Tooltip>

					<Button
						size="small"
						variant="text"
						color="inherit"
						onClick={() => props.handleClose(false)}
						startIcon={<CloseIcon />}
						sx={{ textTransform: "none", fontWeight: 700 }}
					>
						<Translate String="Auto.Features.Shift.Ui.Components.ShiftSummaryDialogue.L216C7.Close">Close</Translate>
					</Button>
				</Stack>
			</Box>

			<Divider />

			<DialogContent
				sx={{
					...sharedShiftDialogContentSx(theme),
					height: "100%",
					px: { xs: 1.5, sm: 3 },
					py: { xs: 1.5, sm: 2 },
				}}
			>
				{/* Loading state */}
				{(isLoading || !data) && (
					<Box sx={{ ...sharedShiftDialogPanelSx(theme), py: 2, px: 2 }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
							<Box
								sx={{
									width: 10,
									height: 10,
									borderRadius: "50%",
									bgcolor: "text.secondary",
									opacity: 0.6,
								}}
							/>
							<Typography
								variant="body2"
								color="text.secondary"
								fontWeight={700}
							>
								Writing summary…
							</Typography>
						</Box>

						<Box sx={{ mt: 2 }}>
							<Typography variant="body2" color="text.secondary">
								Please wait a moment.
							</Typography>
						</Box>
					</Box>
				)}

				{/* Typed output */}
				{!isLoading && data && (
					<DialogContentText
						id="alert-dialog-slide-description"
						sx={{
							...sharedShiftDialogPanelSx(theme),
							m: 0,
							p: { xs: 1.5, sm: 2 },
						}}
					>
						{!done ? (
							<Typography
								variant="body2"
								component="div"
								sx={{
									whiteSpace: "pre-wrap",
									fontFamily:
										"ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
									lineHeight: 1.6,
								}}
							>
								{typed}
								<Box
									component="span"
									sx={{
										display: "inline-block",
										width: 8,
										height: 16,
										ml: 0.5,
										bgcolor: "text.primary",
										animation: "blink 1s steps(1) infinite",
										"@keyframes blink": {
											"0%, 50%, 100%": { opacity: 1 },
											"25%, 75%": { opacity: 0 },
										},
									}}
								/>
							</Typography>
						) : (
							<SATOSContentRender content={htmlSummary} />
						)}
					</DialogContentText>
				)}
			</DialogContent>
		</StyledDialog>
	);
};

interface StyledDialogProps {
	open: boolean;
	handleClose: (open: boolean) => void;
	children?: React.ReactNode;
}

export const StyledDialog: FC<StyledDialogProps> = (props) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	return (
		<Dialog
			open={props.open}
			keepMounted
			onClose={() => props.handleClose(false)}
			aria-describedby="alert-dialog-slide-description"
			scroll="paper"
			fullScreen={isMobile}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					...sharedShiftDialogPaperSx(theme, isMobile),
					...(isMobile
						? {
								borderRadius: 0,
							}
						: {
								position: "absolute",
								right: "15px",
								top: "160px",
								width: "600px",
								minHeight: "200px",
							}),
				},
			}}
		>
			{props.children}
		</Dialog>
	);
};
